import {
  Box,
  Badge,
  Button,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/client/components/shared/DataTable';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { FormField } from '@/components/Modal/FormField';
import { TestNotificationModal } from '../components/TestNotificationModal';
import type { BotMessageCount, DeliveryLog } from '@/lib/sdk/delivery/types';
import { useDeliveryLogsPage } from '../hooks';

const statusColorMap: Record<string, string> = {
  sent: 'gray', delivered: 'blue', read: 'green', failed: 'red',
};

const categoryColorMap: Record<string, string> = {
  workflow: 'purple', flow_launch: 'teal', nudge: 'orange',
};

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

const columns: ColumnDef<DeliveryLog, unknown>[] = [
  {
    header: 'Sent At', accessorKey: 'sent_at', size: 160,
    cell: (info) => <Text fontSize="sm" whiteSpace="nowrap">{formatDateTime(info.getValue() as string)}</Text>,
  },
  {
    header: 'Recipient', accessorKey: 'recipient', size: 160,
    cell: (info) => {
      const row = info.row.original;
      return (
        <VStack align="start" spacing={0}>
          <Text fontSize="sm">{row.recipient}</Text>
          {row.recipient_name && <Text fontSize="xs" color="text-secondary">{row.recipient_name}</Text>}
        </VStack>
      );
    },
  },
  {
    header: 'Category', accessorKey: 'category', size: 100,
    cell: (info) => {
      const cat = info.getValue() as string;
      return <Badge colorScheme={categoryColorMap[cat] ?? 'gray'} textTransform="capitalize">{cat.replace('_', ' ')}</Badge>;
    },
  },
  {
    header: 'Chatbot', accessorKey: 'chatbot_code', size: 100,
    cell: (info) => <Text fontSize="sm">{(info.getValue() as string | null) || '—'}</Text>,
  },
  {
    header: 'Preview', accessorKey: 'preview', size: 300,
    cell: (info) => <Text fontSize="sm" noOfLines={2} maxW="300px">{(info.getValue() as string) || '—'}</Text>,
  },
  {
    header: 'Status', accessorKey: 'status', size: 100,
    cell: (info) => {
      const status = info.getValue() as string;
      return <Badge colorScheme={statusColorMap[status] ?? 'gray'} textTransform="capitalize">{status}</Badge>;
    },
  },
  {
    header: 'Status At', accessorKey: 'status_at', size: 160,
    cell: (info) => <Text fontSize="sm" whiteSpace="nowrap">{formatDateTime(info.getValue() as string | null)}</Text>,
  },
  {
    header: 'Error', accessorKey: 'error_msg', size: 200,
    cell: (info) => {
      const row = info.row.original;
      if (!row.error_msg) return <Text color="text-secondary">—</Text>;
      return <Text fontSize="xs" color="red.500" noOfLines={2} maxW="200px">[{row.error_code}] {row.error_msg}</Text>;
    },
  },
];

const weeklyCountColumns: ColumnDef<BotMessageCount, unknown>[] = [
  {
    header: 'Workflow', accessorKey: 'chatbot_title', size: 240,
    cell: (info) => {
      const row = info.row.original;
      if (!row.chatbot_code) return <Text fontSize="sm" color="text-secondary">Unattributed (org-wide nudges, summaries)</Text>;
      return <Text fontSize="sm">{row.chatbot_title || '—'}</Text>;
    },
  },
  {
    header: 'Chatbot', accessorKey: 'chatbot_code', size: 120,
    cell: (info) => <Text fontSize="sm">{(info.getValue() as string) || '—'}</Text>,
  },
  {
    header: 'Messages', accessorKey: 'message_count', size: 100,
    cell: (info) => <Text fontSize="sm" fontWeight="medium">{(info.getValue() as number).toLocaleString()}</Text>,
  },
];

export function DeliveryLogsPage() {
  const p = useDeliveryLogsPage();

  return (
    <PageContainer title="Delivery Logs">
      <VStack spacing={4} align="stretch">
        <HStack justify="flex-end">
          <Button variant="outline" size="sm" onClick={p.handleExport} isLoading={p.isExporting}>
            Export to Excel
          </Button>
          <Button colorScheme="brand" size="sm" onClick={p.openTestModal}>
            Send Test Notification
          </Button>
        </HStack>

        <TestNotificationModal orgId={p.orgId} isOpen={p.isTestModalOpen} onClose={p.closeTestModal} />

        <Box p={4} borderWidth="1px" borderColor="border-default" borderRadius="lg" bg="bg-surface">
          <HStack justify="space-between" mb={3}>
            <Text fontWeight="semibold">Messages sent, last 7 days</Text>
            <Text fontSize="sm" color="text-secondary">{p.weeklyTotal.toLocaleString()} total</Text>
          </HStack>
          <DataTable
            data={p.weeklyCounts}
            columns={weeklyCountColumns}
            isLoading={p.isWeeklyCountsLoading}
            emptyMessage="No messages sent in the last 7 days."
            enableSorting={false} enableFiltering={false} enablePagination={false}
            enableColumnVisibility={false} stickyHeader={true} maxTableHeight="240px"
            getRowId={(row) => row.chatbot_code || 'unattributed'}
          />
        </Box>

        <Box
          as="form"
          onSubmit={p.handleSubmit(p.onApplyFilters)}
          p={4} borderWidth="1px" borderColor="border-default" borderRadius="lg" bg="bg-surface"
        >
          <HStack spacing={4} flexWrap="wrap" align="flex-end">
            <Box minW="140px">
              <FormField label="Category" name="category" register={p.register} type="select"
                options={[{ value: '', label: 'All' }, { value: 'workflow', label: 'Workflow' }, { value: 'flow_launch', label: 'Flow launch' }, { value: 'nudge', label: 'Nudge' }]} />
            </Box>
            <Box minW="140px">
              <FormField label="Status" name="status" register={p.register} type="select"
                options={[{ value: '', label: 'All' }, { value: 'sent', label: 'Sent' }, { value: 'delivered', label: 'Delivered' }, { value: 'read', label: 'Read' }, { value: 'failed', label: 'Failed' }]} />
            </Box>
            <Box minW="160px">
              <FormField label="From" name="from" register={p.register} type="date" />
            </Box>
            <Box minW="160px">
              <FormField label="To" name="to" register={p.register} type="date" />
            </Box>
            <Box minW="140px">
              <FormField label="Chatbot Code" name="chatbot_code" register={p.register} type="text" placeholder="e.g. SOP001" />
            </Box>
            <Box>
              <button type="submit" style={{ padding: '8px 16px', borderRadius: '6px', background: 'var(--chakra-colors-brand-500)', color: 'white', fontWeight: 500, cursor: 'pointer', border: 'none' }}>
                Apply
              </button>
            </Box>
          </HStack>
        </Box>

        <HStack justify="space-between">
          <Text fontSize="sm" color="text-secondary">
            {p.total} total log{p.total !== 1 ? 's' : ''}
          </Text>
          <HStack spacing={2}>
            <Text fontSize="sm" color="text-secondary">Page {p.page} of {p.pageCount}</Text>
            <button onClick={() => p.setPage((prev) => Math.max(1, prev - 1))} disabled={p.page <= 1}
              style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid var(--chakra-colors-border-default)', background: 'transparent', cursor: p.page <= 1 ? 'not-allowed' : 'pointer', opacity: p.page <= 1 ? 0.4 : 1 }}>
              ‹
            </button>
            <button onClick={() => p.setPage((prev) => Math.min(p.pageCount, prev + 1))} disabled={p.page >= p.pageCount}
              style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid var(--chakra-colors-border-default)', background: 'transparent', cursor: p.page >= p.pageCount ? 'not-allowed' : 'pointer', opacity: p.page >= p.pageCount ? 0.4 : 1 }}>
              ›
            </button>
          </HStack>
        </HStack>

        <DataTable
          data={p.items}
          columns={columns}
          isLoading={p.isLoading}
          emptyMessage="No delivery logs found."
          enableSorting={false} enableFiltering={false} enablePagination={false}
          enableColumnVisibility={true} stickyHeader={true} maxTableHeight="600px"
          getRowId={(row) => String(row.id)}
        />
      </VStack>
    </PageContainer>
  );
}
